import { useTable, useNavigation, useDelete, useNotification, useInvalidate } from '@refinedev/core'
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { toTitleCase } from '@/lib/utils'
import { ChevronUp, ChevronDown, Pencil, Eye, Trash2, X, Check, Columns3 } from 'lucide-react'
import type { ReactNode } from 'react'
import { useState, useEffect, useRef, useMemo, useLayoutEffect } from 'react'
import type { LogicalFilter, ConditionalFilter } from '@refinedev/core'
import { RESOURCE_READ_VIEW } from '@/resources'
import { RESOURCE_FIELD_ORDER } from '@/resources/fieldSchema'
import { TableSearch } from './TableSearch'
import { FilterPanel } from './FilterPanel'
import { TablePagination } from './TablePagination'

interface DataTableProps {
  resource: string
  searchField?: string
  searchFields?: string[]
  extraActions?: (record: Record<string, unknown>) => ReactNode
}

export function pickSearchColumn(columns: string[], explicit?: string): string | undefined {
  if (explicit) return explicit
  const nameLike = columns.find((c) => c.endsWith('_name') || c === 'name' || c === 'title')
  if (nameLike) return nameLike
  const fallbacks = [
    'email', 'email_domain', 'username', 'fullname', 'company_name',
    'customer_name', 'partner_test_name', 'agency_name', 'organization_name',
    'subject', 'message', 'body', 'content', 'instructions',
    'token', 'slug', 'description', 'connection', 'provider', 'endpoint',
    'api_call', 'event_type', 'public_key', 'url', 'path', 'reference',
    'question_text', 'template_name', 'phone', 'text_content', 'service_name',
    'fields_type', 'transfer_status', 'status', 'type',
  ]
  for (const key of fallbacks) {
    if (columns.includes(key)) return key
  }
  return undefined
}

function formatCellValue(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (typeof value === 'boolean') return ''
  if (typeof value !== 'object') return String(value)
  if (Array.isArray(value)) {
    if (value.length === 0) return '[]'
    const first = value[0]
    if (typeof first === 'object' && first !== null) return `[${value.length} items]`
    return value.slice(0, 3).join(', ') + (value.length > 3 ? '…' : '')
  }
  const obj = value as Record<string, unknown>
  if (typeof obj.name === 'string') return obj.name
  if (typeof obj.label === 'string') return obj.label
  if (typeof obj.title === 'string') return obj.title
  if (typeof obj.email === 'string') return obj.email
  if (typeof obj.id !== 'undefined') return String(obj.id)
  return JSON.stringify(obj).slice(0, 60)
}

export function resolveLabelColumn(col: string, colSet: Set<string>): string | null {
  const stem = col.endsWith('_id') ? col.slice(0, -3)
             : col.endsWith('_by') ? col
             : null
  if (!stem) return null
  for (const suffix of ['_fullname', '_name', '_email', '_title', '_label']) {
    const candidate = `${stem}${suffix}`
    if (colSet.has(candidate)) return candidate
  }
  return null
}

export function buildDisplayColumns(columns: string[], visibility: Record<string, boolean>): {
  displayColumns: string[]
  headerOverrides: Record<string, string>
} {
  const colSet = new Set(columns)
  const suppressedLabelCols = new Set<string>()
  const headerOverrides: Record<string, string> = {}
  const displayColumns: string[] = []

  for (const col of columns) {
    if (visibility[col] === false) continue
    const labelCol = resolveLabelColumn(col, colSet)
    if (labelCol) {
      if (visibility[labelCol] !== false) {
        displayColumns.push(labelCol)
        suppressedLabelCols.add(labelCol)
        headerOverrides[labelCol] = toTitleCase(col.replace(/_id$/, ''))
      } else {
        displayColumns.push(col)
      }
    } else if (!suppressedLabelCols.has(col)) {
      displayColumns.push(col)
    }
  }

  return { displayColumns, headerOverrides }
}

const VISIBILITY_KEY = (resource: string) => `col_visibility_${resource}`

function loadVisibility(resource: string): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(VISIBILITY_KEY(resource))
    return raw ? JSON.parse(raw) : {}
  } catch {
    return {}
  }
}

function saveVisibility(resource: string, state: Record<string, boolean>) {
  try {
    localStorage.setItem(VISIBILITY_KEY(resource), JSON.stringify(state))
  } catch { /* silent */ }
}

function ColumnVisibilityMenu({
  columns, visibility, onChange, onShowAll,
}: {
  columns: string[]
  visibility: Record<string, boolean>
  onChange: (col: string, visible: boolean) => void
  onShowAll: () => void
}) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const hidden = columns.filter((c) => visibility[c] === false).length

  return (
    <div className="relative" ref={ref}>
      <Button
        variant="outline"
        size="sm"
        className="gap-1.5 text-xs h-8"
        onClick={() => setOpen((o) => !o)}
      >
        <Columns3 size={13} />
        Columns
        {hidden > 0 && (
          <span className="ml-0.5 px-1.5 py-0.5 rounded-md bg-[#076698] text-white text-[9px] font-bold leading-none">
            {hidden} hidden
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-10 z-50 w-56 glass-card p-2 space-y-0.5">
          <p className="text-[10px] font-extrabold uppercase tracking-widest text-slate-400 dark:text-slate-500 px-2 pb-1.5">
            Toggle Columns
          </p>

          <div className="max-h-72 overflow-y-auto space-y-0.5">
            {columns.map((col) => {
              const isVisible = visibility[col] !== false
              return (
                <label
                  key={col}
                  className="flex items-center gap-2 px-2 py-1.5 rounded-lg cursor-pointer
                             hover:bg-slate-50 dark:hover:bg-slate-700/50
                             text-xs text-slate-700 dark:text-slate-200 select-none"
                >
                  <input
                    type="checkbox"
                    checked={isVisible}
                    onChange={(e) => onChange(col, e.target.checked)}
                    className="accent-[#076698] w-3 h-3 shrink-0"
                  />
                  <span className="truncate">{toTitleCase(col)}</span>
                </label>
              )
            })}
          </div>

          {hidden > 0 && (
            <div className="border-t border-border/40 mt-1 pt-1">
              <button
                className="w-full text-xs text-[#076698] hover:underline px-2 py-0.5 text-left transition-colors"
                onClick={() => { onShowAll(); setOpen(false) }}
              >
                Show all columns
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

function parseFkError(err: unknown): string | null {
  const details = (err as { details?: string })?.details ?? ''
  const match = details.match(/from table "([^"]+)"/)
  return match ? match[1] : null
}

export function DataTable({ resource, searchFields, extraActions }: DataTableProps) {
  const readResource = RESOURCE_READ_VIEW[resource] ?? resource
  const { edit, show } = useNavigation()
  const { open: notify } = useNotification()
  const { mutate: deleteRecord } = useDelete()
  const invalidate = useInvalidate()
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null)
  const {
    tableQueryResult: { data, isLoading },
    pageCount, current, setCurrent,
    pageSize, setPageSize,
    setSorters, sorters,
    filters, setFilters,
  } = useTable({
    resource: readResource,
    syncWithLocation: false,
    pagination: { pageSize: 10 },
  })

  const records = (data?.data ?? []) as Record<string, unknown>[]
  const prevResource = useRef(readResource)

  useEffect(() => {
    if (prevResource.current !== readResource) {
      prevResource.current = readResource
      setFilters([], 'replace')
      setSorters([])
      setCurrent(1)
    }
  }, [readResource])
  const total = data?.total ?? 0
  const columns = useMemo(() => {
    if (records.length === 0) return []
    const raw = Object.keys(records[0])
    const schemaOrder = RESOURCE_FIELD_ORDER[resource]
    if (!schemaOrder) return raw
    const orderMap = new Map(schemaOrder.map((k, i) => [k, i]))
    return [...raw].sort((a, b) => {
      const ai = orderMap.get(a)
      const bi = orderMap.get(b)
      if (ai !== undefined && bi !== undefined) return ai - bi
      if (ai !== undefined) return -1
      if (bi !== undefined) return 1
      return 0
    })
  }, [records, resource])

  const [visibility, setVisibility] = useState<Record<string, boolean>>(() => loadVisibility(resource))

  const { displayColumns, headerOverrides } = buildDisplayColumns(columns, visibility)

  const handleVisibilityChange = (col: string, visible: boolean) => {
    const next = { ...visibility, [col]: visible }
    setVisibility(next)
    saveVisibility(resource, next)
  }

  const handleShowAll = () => {
    const reset: Record<string, boolean> = {}
    columns.forEach((c) => { reset[c] = true })
    setVisibility(reset)
    saveVisibility(resource, reset)
  }

  const searchableColumns = useMemo(() => {
    if (columns.length === 0 || records.length === 0) return columns
    const badTypes = new Set<string>()
    const hasValue = new Set<string>()
    for (const r of records) {
      for (const c of columns) {
        if (badTypes.has(c)) continue
        const v = r[c]
        if (v == null) continue
        hasValue.add(c)
        if (Array.isArray(v) || typeof v === 'boolean' || typeof v === 'number' || typeof v === 'object') {
          badTypes.add(c)
        } else if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
          badTypes.add(c)
        }
      }
    }
    return columns.filter((c) => {
      if (!hasValue.has(c)) return false
      if (badTypes.has(c)) return false
      if (c === 'number' || c === 'size') return false
      if (c.startsWith('is_') || c.startsWith('enable_') || c.startsWith('use_') || c.startsWith('show_') || c.startsWith('has_') || c.startsWith('can_') || c.startsWith('edit_')) return false
      if (c.endsWith('_at') || c.endsWith('_date') || c.endsWith('_count') || c.endsWith('_by') || c.endsWith('_config') || c.endsWith('_login')) return false
      return true
    })
  }, [columns, records])

  const numericId = useMemo(() =>
    records.length > 0 && records[0] && typeof records[0].id === 'number'
  , [records])

  const effectiveSearchFields = useMemo(() => {
    if (searchFields && searchFields.length > 0) {
      if (columns.length === 0) return searchFields
      const valid = searchFields.filter((f) => searchableColumns.includes(f))
      return valid.length > 0 ? valid : searchableColumns
    }
    return searchableColumns
  }, [searchFields, searchableColumns, columns])

  const stableSearchFields = useRef<string[]>([])
  const activeSearchFields = useMemo(() => {
    if (effectiveSearchFields.length > 0) {
      stableSearchFields.current = effectiveSearchFields
      const fields = [...effectiveSearchFields]
      if (columns.includes('id') && !fields.includes('id')) fields.unshift('id')
      return fields
    }
    const fields = [...stableSearchFields.current]
    if (columns.includes('id') && !fields.includes('id')) fields.unshift('id')
    return fields
  }, [effectiveSearchFields, columns])

  const searchFilterFields = useMemo(() => new Set(activeSearchFields), [activeSearchFields])

  useLayoutEffect(() => {
    const stale = filters.filter((f) =>
      'field' in f && !searchFilterFields.has((f as LogicalFilter).field)
    )
    if (stale.length === 0) return
    const clean = filters.filter((f) =>
      !('field' in f) || searchFilterFields.has((f as LogicalFilter).field)
    )
    setFilters(clean as LogicalFilter[], 'replace')
    setCurrent(1)
  }, [searchFilterFields])

  const panelFilters = filters.filter(
    (f): f is LogicalFilter =>
      'field' in f && !searchFilterFields.has((f as LogicalFilter).field)
  )

  const handleSearch = (value: string) => {
    if (activeSearchFields.length === 0) return

    setCurrent(1)

    const sanitized = value.slice(0, 200)
    const escaped = sanitized.replace(/[%_]/g, '\\$&')

    const fields = numericId && isNaN(Number(sanitized))
      ? activeSearchFields.filter((f) => f !== 'id')
      : activeSearchFields

    const getOperator = (field: string) => field === 'id' && numericId ? ('eq' as const) : ('contains' as const)

    if (fields.length === 0) return

    const withoutSearch = filters.filter(
      (f) => {
        if ('field' in f) return !searchFilterFields.has((f as LogicalFilter).field)
        if ('operator' in f && f.operator === 'or') return false
        return true
      }
    )

    if (!value) {
      setFilters(withoutSearch as LogicalFilter[], 'replace')
      return
    }

    if (fields.length === 1) {
      setFilters(
        [...withoutSearch, { field: fields[0], operator: getOperator(fields[0]), value: escaped }] as LogicalFilter[],
        'replace'
      )
    } else {
      const orFilter: ConditionalFilter = {
        operator: 'or',
        value: fields.map((field) => ({
          field,
          operator: getOperator(field),
          value: escaped,
        })),
      }
      setFilters([...withoutSearch, orFilter] as (LogicalFilter | ConditionalFilter)[], 'replace')
    }
  }

  const handleSort = (col: string) => {
    const existing = sorters.find((s) => s.field === col)
    setSorters([{ field: col, order: existing?.order === 'asc' ? 'desc' : 'asc' }])
  }

  const removeFilter = (field: string) => {
    setFilters(
      panelFilters.filter((f) => f.field !== field),
      'replace'
    )
  }

  const hasSearch = activeSearchFields.length > 0

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 flex-wrap">
        {hasSearch && (
          <TableSearch
            onSearch={handleSearch}
            placeholder={`Search ${toTitleCase(resource)}…`}
          />
        )}
        <FilterPanel
          columns={columns}
          activeFilters={panelFilters}
          onApply={(newFilters) => {
            const searchFilter = filters.filter((f) => {
              if ('field' in f) return searchFilterFields.has((f as LogicalFilter).field)
              return false
            })
            setFilters([...searchFilter, ...newFilters] as LogicalFilter[], 'replace')
          }}
          onReset={() => {
            const searchFilter = filters.filter((f) => {
              if ('field' in f) return searchFilterFields.has((f as LogicalFilter).field)
              return false
            })
            setFilters(searchFilter as LogicalFilter[], 'replace')
          }}
        />
        <div className="ml-auto">
          {columns.length > 0 && (
            <ColumnVisibilityMenu
              columns={columns}
              visibility={visibility}
              onChange={handleVisibilityChange}
              onShowAll={handleShowAll}
            />
          )}
        </div>
      </div>

      {panelFilters.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-muted-foreground">Active:</span>
          {panelFilters.map((f) => (
            <span
              key={f.field}
              className="flex items-center gap-1 bg-[#e0f0f7] text-[#076698] rounded-full px-2.5 py-0.5 text-xs"
            >
              {f.field} {f.operator} &ldquo;{String(f.value)}&rdquo;
              <button onClick={() => removeFilter(f.field)} className="ml-0.5 hover:text-[#044268]">
                <X size={10} />
              </button>
            </span>
          ))}
          <button
            onClick={() => {
              const searchFilter = filters.filter((f) => {
                if ('field' in f) return searchFilterFields.has((f as LogicalFilter).field)
                return false
              })
              setFilters(searchFilter as LogicalFilter[], 'replace')
            }}
            className="text-xs text-destructive hover:underline"
          >
            Clear all
          </button>
        </div>
      )}

      <div className="table-surface">
        <Table>
          <TableHeader>
            <TableRow>
              {displayColumns.map((col) => (
                <TableHead
                  key={col}
                  className="cursor-pointer select-none text-xs"
                  onClick={() => handleSort(col)}
                >
                  <span className="flex items-center gap-1">
                    {headerOverrides[col] ?? toTitleCase(col)}
                    {sorters.find((s) => s.field === col)?.order === 'asc'
                      ? <ChevronUp size={12} />
                      : <ChevronDown size={12} className="text-muted-foreground" />}
                  </span>
                </TableHead>
              ))}
              <TableHead className="text-xs">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={displayColumns.length + 1}
                  className="text-center text-muted-foreground text-sm py-8">
                  Loading...
                </TableCell>
              </TableRow>
            ) : records.length === 0 ? (
              <TableRow>
                <TableCell colSpan={displayColumns.length + 1}
                  className="text-center text-muted-foreground text-sm py-8">
                  No records
                </TableCell>
              </TableRow>
            ) : (
              records.map((record, i) => (
                <TableRow key={(record.id as string) ?? i}>
                  {displayColumns.map((col) => (
                    <TableCell key={col} className="text-xs max-w-[200px] truncate">
                      {typeof record[col] === 'boolean' ? (
                        <Badge variant={record[col] ? 'default' : 'secondary'}>
                          {record[col] ? 'Yes' : 'No'}
                        </Badge>
                      ) : Array.isArray(record[col]) ? (
                        <span className="text-muted-foreground text-xs">
                          {formatCellValue(record[col])}
                        </span>
                      ) : typeof record[col] === 'object' && record[col] !== null ? (
                        <span className="text-xs text-muted-foreground truncate block max-w-[180px]">
                          {formatCellValue(record[col])}
                        </span>
                      ) : (
                        String(record[col] ?? '')
                      )}
                    </TableCell>
                  ))}
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7"
                        onClick={() => show(resource, record.id as string)}>
                        <Eye size={13} />
                      </Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7"
                        onClick={() => edit(resource, record.id as string)}>
                        <Pencil size={13} />
                      </Button>
                      {confirmDeleteId === record.id ? (
                        <>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                            title="Confirm delete"
                            onClick={() => {
                              setConfirmDeleteId(null)
                              deleteRecord(
                                { resource, id: record.id as string },
                                {
                                  onSuccess: () => {
                                    invalidate({ resource: readResource, invalidates: ['list'] })
                                  },
                                  onError(err) {
                                    const ref = parseFkError(err)
                                    notify?.({
                                      type: 'error',
                                      message: 'Cannot delete — record is in use',
                                      description: ref
                                        ? `Still referenced by "${ref}". Remove those records first.`
                                        : String((err as { message?: string })?.message ?? err),
                                    })
                                  },
                                },
                              )
                            }}>
                            <Check size={13} />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-7 w-7"
                            title="Cancel"
                            onClick={() => setConfirmDeleteId(null)}>
                            <X size={13} />
                          </Button>
                        </>
                      ) : (
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                          title="Delete"
                          onClick={() => setConfirmDeleteId(record.id as string)}>
                          <Trash2 size={13} />
                        </Button>
                      )}
                      {extraActions?.(record)}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <TablePagination
        current={current}
        pageCount={pageCount}
        pageSize={pageSize}
        total={total}
        onPageChange={setCurrent}
        onPageSizeChange={setPageSize}
      />
    </div>
  )
}
